import { useState, useEffect, useRef, useCallback } from "react";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import Layout from "../components/Layout";
import EditableText from "../components/EditableText";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Checkbox } from "../components/ui/checkbox";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  Users, Globe, Lock, Plus, Search, Send, LogOut as LeaveIcon, Trash2,
  UserPlus, MessageCircle, Sparkles, Hash, X
} from "lucide-react";

const GroupsPage = () => {
  const { user } = useAuth();

  const [myGroups, setMyGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [loadingMine, setLoadingMine] = useState(true);
  const [loadingPublic, setLoadingPublic] = useState(false);
  const [searchPublic, setSearchPublic] = useState("");

  const [activeGroup, setActiveGroup] = useState(null); // group detail object with members_info
  const [messages, setMessages] = useState([]);
  const [msgInput, setMsgInput] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const msgPollRef = useRef(null);
  const lastMsgTs = useRef("");

  // Create dialog
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", topic: "", is_public: false, member_ids: [] });
  const [creating, setCreating] = useState(false);

  // Members directory (for invitation)
  const [directory, setDirectory] = useState([]);

  // Add members dialog
  const [showAddMembers, setShowAddMembers] = useState(false);
  const [addMembersIds, setAddMembersIds] = useState([]);

  useEffect(() => {
    fetchMyGroups();
    fetchDirectory();
  }, []);

  const fetchDirectory = async () => {
    try {
      const res = await axios.get(`${API}/users/members`);
      setDirectory((res.data || []).filter(m => m.user_id !== user?.user_id));
    } catch { /* silent */ }
  };

  const fetchMyGroups = async () => {
    setLoadingMine(true);
    try {
      const res = await axios.get(`${API}/groups/mine`);
      setMyGroups(res.data);
    } catch { toast.error("Failed to load your groups"); }
    finally { setLoadingMine(false); }
  };

  const fetchPublicGroups = useCallback(async () => {
    setLoadingPublic(true);
    try {
      const params = searchPublic ? `?search=${encodeURIComponent(searchPublic)}` : "";
      const res = await axios.get(`${API}/groups/public${params}`);
      setPublicGroups(res.data);
    } catch { /* silent */ }
    finally { setLoadingPublic(false); }
  }, [searchPublic]);

  useEffect(() => { fetchPublicGroups(); }, [fetchPublicGroups]);

  const openGroup = async (g) => {
    try {
      const res = await axios.get(`${API}/groups/${g.group_id}`);
      setActiveGroup(res.data);
      // Load messages
      const mres = await axios.get(`${API}/groups/${g.group_id}/messages?limit=100`);
      setMessages(mres.data);
      lastMsgTs.current = mres.data.length > 0 ? mres.data[mres.data.length - 1].created_at : "";
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to open group");
    }
  };

  // Poll new messages when a group is open
  useEffect(() => {
    if (!activeGroup) return;
    msgPollRef.current = setInterval(async () => {
      if (!lastMsgTs.current) return;
      try {
        const res = await axios.get(`${API}/groups/${activeGroup.group_id}/messages?after=${encodeURIComponent(lastMsgTs.current)}&limit=50`);
        if (res.data.length > 0) {
          setMessages(prev => {
            const ids = new Set(prev.map(m => m.message_id));
            return [...prev, ...res.data.filter(m => !ids.has(m.message_id))];
          });
          lastMsgTs.current = res.data[res.data.length - 1].created_at;
        }
      } catch { /* silent */ }
    }, 3000);
    return () => clearInterval(msgPollRef.current);
  }, [activeGroup]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleCreate = async () => {
    if (!createForm.name.trim()) { toast.error("Group name is required"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/groups`, createForm);
      toast.success(`Group "${res.data.name}" created!`);
      setShowCreate(false);
      setCreateForm({ name: "", topic: "", is_public: false, member_ids: [] });
      setMyGroups(prev => [res.data, ...prev]);
      openGroup(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create group");
    } finally { setCreating(false); }
  };

  const handleSend = async () => {
    if (!msgInput.trim() || !activeGroup || sending) return;
    setSending(true);
    const text = msgInput.trim();
    setMsgInput("");
    try {
      const res = await axios.post(`${API}/groups/${activeGroup.group_id}/messages`, { content: text });
      setMessages(prev => {
        const ids = new Set(prev.map(m => m.message_id));
        if (ids.has(res.data.message_id)) return prev;
        return [...prev, res.data];
      });
      lastMsgTs.current = res.data.created_at;
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send");
      setMsgInput(text);
    } finally { setSending(false); }
  };

  const handleJoin = async (groupId) => {
    try {
      await axios.post(`${API}/groups/${groupId}/join`);
      toast.success("Joined!");
      fetchMyGroups();
      fetchPublicGroups();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleLeave = async () => {
    if (!activeGroup) return;
    if (!window.confirm(activeGroup.is_creator ? "You are the creator — leaving will transfer ownership or delete the group. Continue?" : "Leave this group?")) return;
    try {
      const res = await axios.post(`${API}/groups/${activeGroup.group_id}/leave`);
      toast.success(res.data.group_deleted ? "Group deleted" : "Left group");
      setActiveGroup(null);
      fetchMyGroups();
      fetchPublicGroups();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleDelete = async () => {
    if (!activeGroup) return;
    if (!window.confirm(`Permanently delete "${activeGroup.name}"? All messages will be lost.`)) return;
    try {
      await axios.delete(`${API}/groups/${activeGroup.group_id}`);
      toast.success("Group deleted");
      setActiveGroup(null);
      fetchMyGroups();
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const handleAddMembers = async () => {
    if (!activeGroup || addMembersIds.length === 0) return;
    try {
      const res = await axios.post(`${API}/groups/${activeGroup.group_id}/members`, { member_ids: addMembersIds });
      toast.success("Members added!");
      setActiveGroup({ ...activeGroup, ...res.data });
      // refetch group detail to get members_info
      const detail = await axios.get(`${API}/groups/${activeGroup.group_id}`);
      setActiveGroup(detail.data);
      setShowAddMembers(false);
      setAddMembersIds([]);
    } catch (error) { toast.error(error.response?.data?.detail || "Failed"); }
  };

  const timeStr = (d) => new Date(d).toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit" });

  const availableToAdd = directory.filter(m => !activeGroup?.members?.includes(m.user_id));

  // If the user lands on /groups with zero groups, default the sidebar to
  // the Discover tab so the empty-state immediately surfaces joinable groups.
  const defaultTab = (!loadingMine && myGroups.length === 0) ? "discover" : "mine";

  return (
    <Layout>
      <div className="p-6 md:p-8 lg:p-12" data-testid="groups-page">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 rounded-xl bg-[#6B4E8A] flex items-center justify-center"><Users className="w-5 h-5 text-[#8DB5A0]" /></div>
              <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                <EditableText blockKey="groups.title" fallback="Groups" as="span" />
              </h1>
            </div>
            <EditableText
              blockKey="groups.subtitle"
              fallback="Create private group chats with colleagues or join interest-based public groups to connect around specialties, provinces, and more."
              as="p"
              className="text-[#64748B] max-w-xl"
              multiline
            />
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full shrink-0" data-testid="create-group-btn">
            <Plus className="w-4 h-4 mr-2" />New Group
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-6">
          {/* Left: Groups list */}
          <div className="bg-white border border-[#E5DFEC] rounded-2xl overflow-hidden flex flex-col max-h-[80vh]">
            <Tabs defaultValue={defaultTab} key={defaultTab} className="flex flex-col flex-1 min-h-0">
              <TabsList className="bg-[#F0ECF7] m-3 shrink-0">
                <TabsTrigger value="mine" className="flex-1 data-[state=active]:bg-white" data-testid="tab-my-groups">My Groups</TabsTrigger>
                <TabsTrigger value="discover" className="flex-1 data-[state=active]:bg-white" data-testid="tab-discover-groups">Discover</TabsTrigger>
              </TabsList>
              <TabsContent value="mine" className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 mt-0">
                {loadingMine ? (
                  <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-[#F0ECF7] rounded-xl animate-pulse" />)}</div>
                ) : myGroups.length > 0 ? (
                  <div className="space-y-1">
                    {myGroups.map(g => (
                      <GroupRow key={g.group_id} group={g} active={activeGroup?.group_id === g.group_id} onClick={() => openGroup(g)} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <MessageCircle className="w-10 h-10 mx-auto mb-2 text-[#E5DFEC]" />
                    <p className="text-sm text-[#64748B] mb-3">No groups yet</p>
                    <div className="flex flex-col gap-2 items-center">
                      <Button size="sm" onClick={() => setShowCreate(true)} className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]" data-testid="empty-create-group-btn">Create your first group</Button>
                      <button
                        type="button"
                        onClick={() => {
                          const trigger = document.querySelector('[data-testid=tab-discover-groups]');
                          if (trigger) trigger.click();
                        }}
                        className="text-xs text-[#8DB5A0] hover:text-[#6B4E8A] underline underline-offset-2"
                        data-testid="empty-discover-cta"
                      >
                        Or discover public groups →
                      </button>
                    </div>
                  </div>
                )}
              </TabsContent>
              <TabsContent value="discover" className="flex-1 min-h-0 overflow-y-auto px-3 pb-3 mt-0">
                <div className="relative mb-2">
                  <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#94A3B8]" />
                  <Input value={searchPublic} onChange={e => setSearchPublic(e.target.value)} placeholder="Search groups..." className="pl-8 h-9 text-sm bg-[#FAF7F2] border-[#E5DFEC]" data-testid="search-public-groups" />
                </div>
                {loadingPublic ? (
                  <div className="space-y-2">{[1,2,3].map(i => <div key={i} className="h-14 bg-[#F0ECF7] rounded-xl animate-pulse" />)}</div>
                ) : publicGroups.length > 0 ? (
                  <div className="space-y-1">
                    {publicGroups.map(g => (
                      <PublicGroupRow key={g.group_id} group={g} onJoin={() => handleJoin(g.group_id)} onOpen={() => openGroup(g)} />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-10">
                    <Globe className="w-10 h-10 mx-auto mb-2 text-[#E5DFEC]" />
                    <p className="text-sm text-[#64748B] mb-1">No public groups yet</p>
                    <p className="text-xs text-[#94A3B8]">Be the first to start one!</p>
                  </div>
                )}
              </TabsContent>
            </Tabs>
          </div>

          {/* Right: Active group detail */}
          <div className="bg-white border border-[#E5DFEC] rounded-2xl overflow-hidden flex flex-col min-h-[60vh] max-h-[80vh]">
            {activeGroup ? (
              <>
                {/* Group header */}
                <div className="border-b border-[#E5DFEC] p-4 flex items-start justify-between gap-3 shrink-0">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {activeGroup.is_public ? <Globe className="w-4 h-4 text-[#10B981]" /> : <Lock className="w-4 h-4 text-[#64748B]" />}
                      <h2 className="font-medium text-[#6B4E8A] truncate" style={{ fontFamily: 'Outfit, sans-serif' }} data-testid="active-group-name">{activeGroup.name}</h2>
                      <span className="text-xs text-[#94A3B8]">· {activeGroup.member_count} member{activeGroup.member_count !== 1 ? "s" : ""}</span>
                    </div>
                    {activeGroup.topic && <p className="text-xs text-[#64748B] truncate">{activeGroup.topic}</p>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="p-2 hover:bg-[#F0ECF7] rounded-lg" data-testid="group-members-btn" title="Members"><Users className="w-4 h-4 text-[#64748B]" /></button>
                      </PopoverTrigger>
                      <PopoverContent className="w-64" align="end">
                        <p className="text-xs font-medium text-[#6B4E8A] mb-2">Members ({activeGroup.members_info?.length || 0})</p>
                        <div className="space-y-1 max-h-56 overflow-y-auto">
                          {(activeGroup.members_info || []).map(m => (
                            <div key={m.user_id} className="flex items-center gap-2 text-sm">
                              {m.picture ? <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <div className="w-6 h-6 rounded-full bg-[#8DB5A0] text-white text-xs flex items-center justify-center">{m.name?.[0] || "M"}</div>}
                              <span className="truncate">{m.name}{m.user_id === activeGroup.creator_id && <span className="text-[10px] text-[#8DB5A0] ml-1">· owner</span>}</span>
                            </div>
                          ))}
                        </div>
                      </PopoverContent>
                    </Popover>
                    {!activeGroup.is_public && (
                      <button onClick={() => setShowAddMembers(true)} className="p-2 hover:bg-[#F0ECF7] rounded-lg" data-testid="add-members-btn" title="Add members"><UserPlus className="w-4 h-4 text-[#64748B]" /></button>
                    )}
                    <button onClick={handleLeave} className="p-2 hover:bg-[#F0ECF7] rounded-lg" data-testid="leave-group-btn" title="Leave"><LeaveIcon className="w-4 h-4 text-[#64748B]" /></button>
                    {activeGroup.is_creator && (
                      <button onClick={handleDelete} className="p-2 hover:bg-[#EF4444]/10 rounded-lg" data-testid="delete-group-btn" title="Delete"><Trash2 className="w-4 h-4 text-[#EF4444]" /></button>
                    )}
                  </div>
                </div>

                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 bg-[#FAF7F2] space-y-2" data-testid="group-messages">
                  {messages.length === 0 && (
                    <div className="text-center py-12">
                      <Sparkles className="w-10 h-10 mx-auto mb-2 text-[#E5DFEC]" />
                      <p className="text-sm text-[#64748B]">No messages yet. Start the conversation!</p>
                    </div>
                  )}
                  {messages.map(m => {
                    const isMe = m.sender_id === user?.user_id;
                    return (
                      <div key={m.message_id} className={`flex ${isMe ? "justify-end" : "justify-start"}`}>
                        <div className={`max-w-[75%] ${isMe ? "order-2" : ""}`}>
                          {!isMe && <p className="text-[10px] font-medium text-[#64748B] mb-0.5 ml-1">{m.sender_name}</p>}
                          <div className={`px-3 py-2 rounded-2xl text-sm whitespace-pre-wrap break-words ${isMe ? "bg-[#6B4E8A] text-white rounded-br-md" : "bg-white border border-[#E5DFEC] text-[#6B4E8A] rounded-bl-md"}`}>{m.content}</div>
                          <p className={`text-[10px] text-[#94A3B8] mt-0.5 ${isMe ? "text-right mr-1" : "ml-1"}`}>{timeStr(m.created_at)}</p>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t border-[#E5DFEC] p-3 bg-white shrink-0">
                  <div className="flex gap-2">
                    <Input
                      value={msgInput} onChange={e => setMsgInput(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && handleSend()}
                      placeholder={`Message ${activeGroup.name}`}
                      className="flex-1 bg-[#FAF7F2] border-[#E5DFEC] h-9"
                      data-testid="group-message-input"
                    />
                    <Button onClick={handleSend} disabled={!msgInput.trim() || sending} className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] h-9 w-9 p-0" data-testid="group-send-btn"><Send className="w-3.5 h-3.5" /></Button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex flex-col items-center justify-center p-10 text-center">
                <div className="w-16 h-16 rounded-full bg-[#F0ECF7] flex items-center justify-center mb-4"><Hash className="w-8 h-8 text-[#8DB5A0]" /></div>
                <h3 className="text-lg font-medium text-[#6B4E8A] mb-1" style={{ fontFamily: 'Outfit, sans-serif' }}>Select a group</h3>
                <p className="text-sm text-[#64748B] max-w-sm">Pick one of your groups on the left, discover a public one, or create a brand new group.</p>
              </div>
            )}
          </div>
        </div>

        {/* Create Group Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto" data-testid="create-group-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Create a Group</DialogTitle>
              <DialogDescription>Private groups are invite-only. Public groups appear in Discover for anyone to join.</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 mt-2">
              <div>
                <Label>Group name</Label>
                <Input value={createForm.name} onChange={e => setCreateForm(f => ({ ...f, name: e.target.value }))} maxLength={60} placeholder="e.g. AB New Grads" className="mt-2" data-testid="create-group-name" />
              </div>
              <div>
                <Label>Topic / Description</Label>
                <Textarea value={createForm.topic} onChange={e => setCreateForm(f => ({ ...f, topic: e.target.value }))} maxLength={200} placeholder="What's this group about?" className="mt-2 min-h-[60px]" data-testid="create-group-topic" />
              </div>
              <div className="flex items-center gap-2 bg-[#FAF7F2] rounded-xl p-3">
                <Checkbox id="is-public" checked={createForm.is_public} onCheckedChange={c => setCreateForm(f => ({ ...f, is_public: !!c, member_ids: [] }))} data-testid="create-group-public" />
                <Label htmlFor="is-public" className="cursor-pointer text-sm flex-1">
                  <span className="flex items-center gap-1 font-medium text-[#6B4E8A]"><Globe className="w-3.5 h-3.5" />Public interest group</span>
                  <span className="block text-xs text-[#64748B] mt-0.5">Anyone in DHDH can discover and join</span>
                </Label>
              </div>
              {!createForm.is_public && (
                <div>
                  <Label>Invite members <span className="text-[#94A3B8] font-normal">(optional — you can add more later)</span></Label>
                  <div className="mt-2 max-h-52 overflow-y-auto border border-[#E5DFEC] rounded-xl p-2 space-y-1">
                    {directory.length === 0 ? <p className="text-xs text-[#94A3B8] p-2">No other verified members available yet.</p> : directory.map(m => {
                      const checked = createForm.member_ids.includes(m.user_id);
                      return (
                        <label key={m.user_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#FAF7F2] cursor-pointer">
                          <Checkbox checked={checked} onCheckedChange={(c) => {
                            setCreateForm(f => ({ ...f, member_ids: c ? [...f.member_ids, m.user_id] : f.member_ids.filter(id => id !== m.user_id) }));
                          }} />
                          {m.picture ? <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <div className="w-6 h-6 rounded-full bg-[#8DB5A0] text-white text-xs flex items-center justify-center">{m.name?.[0] || "M"}</div>}
                          <span className="text-sm text-[#6B4E8A] truncate flex-1">{m.name}</span>
                          {m.province && <span className="text-[10px] text-[#94A3B8]">{m.province}</span>}
                        </label>
                      );
                    })}
                  </div>
                  {createForm.member_ids.length > 0 && <p className="text-xs text-[#64748B] mt-1">{createForm.member_ids.length} member{createForm.member_ids.length !== 1 ? "s" : ""} selected</p>}
                </div>
              )}
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowCreate(false)} className="rounded-full">Cancel</Button>
                <Button onClick={handleCreate} disabled={creating || !createForm.name.trim()} className="bg-[#8DB5A0] text-white hover:bg-[#D0694E] rounded-full" data-testid="submit-create-group">
                  {creating ? "Creating..." : "Create Group"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Add Members Dialog */}
        <Dialog open={showAddMembers} onOpenChange={setShowAddMembers}>
          <DialogContent className="max-w-md max-h-[75vh] overflow-y-auto" data-testid="add-members-dialog">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Add members to {activeGroup?.name}</DialogTitle>
              <DialogDescription>Select verified members to invite.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 mt-2">
              <div className="max-h-80 overflow-y-auto border border-[#E5DFEC] rounded-xl p-2 space-y-1">
                {availableToAdd.length === 0 ? <p className="text-xs text-[#94A3B8] p-2">Everyone is already in this group.</p> : availableToAdd.map(m => {
                  const checked = addMembersIds.includes(m.user_id);
                  return (
                    <label key={m.user_id} className="flex items-center gap-2 p-2 rounded-lg hover:bg-[#FAF7F2] cursor-pointer">
                      <Checkbox checked={checked} onCheckedChange={(c) => {
                        setAddMembersIds(prev => c ? [...prev, m.user_id] : prev.filter(id => id !== m.user_id));
                      }} />
                      {m.picture ? <img src={m.picture} alt="" className="w-6 h-6 rounded-full object-cover" /> : <div className="w-6 h-6 rounded-full bg-[#8DB5A0] text-white text-xs flex items-center justify-center">{m.name?.[0] || "M"}</div>}
                      <span className="text-sm text-[#6B4E8A] truncate flex-1">{m.name}</span>
                    </label>
                  );
                })}
              </div>
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => { setShowAddMembers(false); setAddMembersIds([]); }} className="rounded-full">Cancel</Button>
                <Button onClick={handleAddMembers} disabled={addMembersIds.length === 0} className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F]" data-testid="submit-add-members">
                  <UserPlus className="w-3.5 h-3.5 mr-1" />Add {addMembersIds.length || ""}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

// --- subcomponents ---

const GroupRow = ({ group, active, onClick }) => (
  <motion.button
    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
    onClick={onClick}
    className={`w-full text-left flex items-center gap-3 px-3 py-2.5 rounded-xl transition-colors ${active ? "bg-[#6B4E8A] text-white" : "hover:bg-[#F0ECF7]"}`}
    data-testid={`group-row-${group.group_id}`}
  >
    <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0" style={{ backgroundColor: group.is_public ? "#10B981" : "#8DB5A0" }}>
      {group.is_public ? <Globe className="w-4 h-4 text-white" /> : <Lock className="w-4 h-4 text-white" />}
    </div>
    <div className="min-w-0 flex-1">
      <p className={`text-sm font-medium truncate ${active ? "text-white" : "text-[#6B4E8A]"}`}>{group.name}</p>
      <p className={`text-xs truncate ${active ? "text-white/60" : "text-[#94A3B8]"}`}>{group.member_count} member{group.member_count !== 1 ? "s" : ""}{group.topic ? ` · ${group.topic}` : ""}</p>
    </div>
  </motion.button>
);

const PublicGroupRow = ({ group, onJoin, onOpen }) => (
  <div className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-[#F0ECF7] transition-colors" data-testid={`public-group-row-${group.group_id}`}>
    <button onClick={onOpen} className="w-9 h-9 rounded-xl bg-[#10B981] flex items-center justify-center shrink-0 mt-0.5"><Globe className="w-4 h-4 text-white" /></button>
    <button onClick={onOpen} className="min-w-0 flex-1 text-left">
      <p className="text-sm font-medium text-[#6B4E8A] truncate">{group.name}</p>
      {group.topic && <p className="text-xs text-[#64748B] line-clamp-2 leading-tight mt-0.5">{group.topic}</p>}
      <p className="text-[10px] text-[#94A3B8] mt-0.5">
        {group.member_count} member{group.member_count !== 1 ? "s" : ""} · by {group.creator_name || "Member"}
      </p>
    </button>
    {group.is_member ? (
      <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#10B981]/10 text-[#10B981] shrink-0 mt-1">Joined</span>
    ) : (
      <Button size="sm" onClick={onJoin} className="rounded-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] h-7 text-xs shrink-0 mt-1" data-testid={`join-${group.group_id}`}>Join</Button>
    )}
  </div>
);

export default GroupsPage;
